import React, { useState } from "react"
import PPAI from "./PPAI"
import { useTranslation } from "react-i18next"
import { demoSignup, demoVerify } from "../../utils/ppAiStandaloneAuth"
import "@fortawesome/fontawesome-free/css/all.css"
import "react-toastify/dist/ReactToastify.css"
import { ToastContainer } from "react-toastify"
import "../../styles/ppai.css"

const STEP_EMAIL = "email"
const STEP_CODE = "code"
const CODE_VALID_MINUTES = 15

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function isValidEmail(value) {
    return typeof value === "string" && EMAIL_REGEX.test(value.trim())
}

function PPAIStandalone() {
    const { t } = useTranslation()
    const [showAuthModal, setShowAuthModal] = useState(false)
    const [step, setStep] = useState(STEP_EMAIL)
    const [email, setEmail] = useState("")
    const [code, setCode] = useState("")
    const [consentAccepted, setConsentAccepted] = useState(false)
    const [loading, setLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState("")


    const handleDemoSignup = async (e) => {
        e?.preventDefault()
        setErrorMsg("")
        if (!consentAccepted) {
            setErrorMsg(t("ppai.standalone_demo.consent_required"))
            return
        }
        const emailTrimmed = email.trim()
        if (!emailTrimmed) {
            setErrorMsg(t("ppai.standalone_demo.email_required"))
            return
        }
        if (!isValidEmail(emailTrimmed)) {
            setErrorMsg(t("ppai.standalone_demo.email_invalid"))
            return
        }
        setLoading(true)
        try {
            await demoSignup(emailTrimmed)
            setStep(STEP_CODE)
            setCode("")
        } catch (err) {
            const msg = err?.message || ""
            setErrorMsg(
                /pro Tag|one per day|par jour/i.test(msg)
                    ? t("ppai.standalone_demo.one_per_day")
                    : msg || t("ppai.standalone_demo.signup_error")
            )
        } finally {
            setLoading(false)
        }
    }

    const handleDemoVerify = async (e) => {
        e?.preventDefault()
        setErrorMsg("")
        if (!code.trim()) {
            setErrorMsg(t("ppai.standalone_demo.code_required"))
            return
        }
        setLoading(true)
        try {
            await demoVerify(email, code)
            setShowAuthModal(false)
            setStep(STEP_EMAIL)
            setEmail("")
            setCode("")
            setConsentAccepted(false)
        } catch (err) {
            setErrorMsg(err?.message || t("ppai.standalone_demo.verify_error"))
        } finally {
            setLoading(false)
        }
    }

    const needAuth = showAuthModal

    return (
        <div className="pp-ai-standalone">
            {needAuth && (
                <div className="pp-ai-standalone-auth-modal-backdrop">
                    <div className="pp-ai-standalone-auth-modal">
                        <div className="pp-ai-standalone-auth-modal-header">
                            <div className="pp-ai-standalone-auth-modal-heading">
                                <h5 className="mb-0">{t("ppai.assistant_name")}</h5>
                                <p className="pp-ai-standalone-auth-modal-subtitle mb-0">
                                    {t("ppai.standalone_demo.subtitle")}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="pp-ai-standalone-auth-modal-close"
                                onClick={() => setShowAuthModal(false)}
                                aria-label={t("ppai.standalone_demo.close")}
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>
                        <div className="pp-ai-standalone-auth-modal-body">
                            {step === STEP_EMAIL && (
                                <form onSubmit={handleDemoSignup}>
                                    <div className="pp-ai-standalone-auth-modal-marketing mb-3">
                                        <span className="pp-ai-standalone-auth-modal-tag">
                                            {t("ppai.standalone_demo.tag")}
                                        </span>
                                        <h4 className="pp-ai-standalone-auth-modal-headline">
                                            {t("ppai.standalone_demo.headline")}
                                        </h4>
                                        <p className="pp-ai-standalone-auth-modal-lead mb-2">
                                            {t("ppai.standalone_demo.lead")}
                                        </p>
                                        <p className="pp-ai-standalone-auth-modal-demo-badge mb-0">
                                            {t("ppai.standalone_demo.demo_badge")}
                                        </p>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="ppai-standalone-email">
                                            {t("ppai.standalone_demo.email_label")}
                                        </label>
                                        <input
                                            id="ppai-standalone-email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value)
                                                if (errorMsg) setErrorMsg("")
                                            }}
                                            placeholder="name@example.com"
                                            disabled={loading}
                                            autoFocus
                                            className={`form-control ${email.trim() && !isValidEmail(email) ? "is-invalid" : ""}`}
                                        />
                                    </div>
                                    <div className="mb-3 form-check mb-3">
                                        <input
                                            id="ppai-standalone-consent"
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={consentAccepted}
                                            onChange={(e) => setConsentAccepted(e.target.checked)}
                                            disabled={loading}
                                        />
                                        <label htmlFor="ppai-standalone-consent" className="form-check-label small">
                                            {t("ppai.standalone_demo.consent_label")}
                                        </label>
                                    </div>
                                    {errorMsg && (
                                        <div className="alert alert-danger py-2 small mb-2">
                                            {errorMsg}
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={loading || !consentAccepted}>
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                {t("ppai.standalone_demo.sending")}
                                            </>
                                        ) : (
                                            t("ppai.standalone_demo.send_code")
                                        )}
                                    </button>
                                    <p className="text-center mt-3 mb-0 small">
                                        <a
                                            href="https://preis-beobachtung.com/#kontakt"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="pp-ai-standalone-demo-link">
                                            {t("ppai.standalone_demo.demo_book_link")}
                                        </a>
                                    </p>
                                </form>
                            )}
                            {step === STEP_CODE && (
                                <form onSubmit={handleDemoVerify}>
                                    <p className="text-muted small mb-2">
                                        {t("ppai.standalone_demo.code_hint", {
                                            email,
                                            minutes: CODE_VALID_MINUTES
                                        })}
                                    </p>
                                    <div className="mb-3">
                                        <label htmlFor="ppai-standalone-code">
                                            {t("ppai.standalone_demo.code_label")}
                                        </label>
                                        <input
                                            id="ppai-standalone-code"
                                            type="text"
                                            className="form-control"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            placeholder="123456"
                                            disabled={loading}
                                            autoFocus
                                        />
                                    </div>
                                    {errorMsg && (
                                        <div className="alert alert-danger py-2 small mb-2">
                                            {errorMsg}
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100 mb-2"
                                        disabled={loading}>
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                {t("ppai.standalone_demo.verifying")}
                                            </>
                                        ) : (
                                            t("ppai.standalone_demo.verify")
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100 btn-sm"
                                        onClick={() => {
                                            setStep(STEP_EMAIL)
                                            setErrorMsg("")
                                        }}
                                        disabled={loading}>
                                        {t("ppai.standalone_demo.back_to_email")}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <main className="pp-ai-standalone-main">
                <PPAI onStandaloneAuthRequired={() => setShowAuthModal(true)} />
            </main>
            <ToastContainer />
        </div>
    )
}

export default PPAIStandalone
